import { useState, useCallback, useRef, useEffect } from 'react';

import { LocationService } from '../services';
import localStorageService from 'services/local-storage-service';

import type { AutocompleteResult, LocationData } from '../services';

// Country code mapping from ISO 3166-1 alpha-2 to alpha-3
const COUNTRY_CODE_MAPPING: Record<string, string> = {
  CO: 'COL',
  MX: 'MEX',
  BR: 'BRA',
  AR: 'ARG',
  PE: 'PER',
  EC: 'ECU',
  UY: 'URY',
  CL: 'CHL',
  CR: 'CRI',
};

interface UsePlacesAutocompleteBackendOptions {
  requestOptions?: {
    componentRestrictions?: { country: string[] };
    maxResults?: number;
  };
  debounce?: number;
}

interface UsePlacesAutocompleteBackendReturn {
  ready: boolean;
  value: string;
  suggestions: {
    status: string;
    data: AutocompleteResult[];
  };
  setValue: (value: string, shouldFetchData?: boolean) => void;
  clearSuggestions: () => void;
}

function mapCountryCode(countryCode2Letter: string): string {
  const upperCode = countryCode2Letter.toUpperCase();
  return COUNTRY_CODE_MAPPING[upperCode] || upperCode;
}

export function usePlacesAutocompleteBackend(
  options: UsePlacesAutocompleteBackendOptions = {},
): UsePlacesAutocompleteBackendReturn {
  const { requestOptions = {}, debounce = 300 } = options;

  const [ready] = useState(true);
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<{
    status: string;
    data: AutocompleteResult[];
  }>({
    status: 'ZERO_RESULTS',
    data: [],
  });

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const clearSuggestions = useCallback(() => {
    setSuggestions({
      status: 'ZERO_RESULTS',
      data: [],
    });
  }, []);

  const fetchSuggestions = useCallback(
    async (searchText: string) => {
      if (!searchText.trim()) {
        clearSuggestions();
        return;
      }

      // Only make request if there are 3 or more characters
      if (searchText.trim().length < 3) {
        clearSuggestions();
        return;
      }

      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        const countryCode2Letter = localStorageService.getCountryCode() || '';
        const countryCode3Letter = mapCountryCode(countryCode2Letter);

        const filterCountries = requestOptions.componentRestrictions?.country
          ? requestOptions.componentRestrictions.country.map(mapCountryCode)
          : countryCode3Letter
          ? [countryCode3Letter]
          : [];

        const maxResults = requestOptions.maxResults || 5;

        const response = await LocationService.getPlacesAutocomplete(
          searchText,
          filterCountries,
          maxResults,
        );

        if (!abortControllerRef.current?.signal.aborted) {
          const results = response?.Results || [];
          setSuggestions({
            status: results.length > 0 ? 'OK' : 'ZERO_RESULTS',
            data: results,
          });
        }
      } catch (err) {
        console.error('Error fetching places autocomplete:', err);
        setSuggestions({
          status: 'ERROR',
          data: [],
        });
      }
    },
    [requestOptions, clearSuggestions],
  );

  const setValueWithDebounce = useCallback(
    (newValue: string, shouldFetchData: boolean = true) => {
      setValue(newValue);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (shouldFetchData) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchSuggestions(newValue);
        }, debounce);
      } else {
        clearSuggestions();
      }
    },
    [fetchSuggestions, debounce, clearSuggestions],
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    ready,
    value,
    suggestions,
    setValue: setValueWithDebounce,
    clearSuggestions,
  };
}

export function useGetLocationData() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getLocationData = useCallback(
    async (
      placeId: string,
      originalText: string,
    ): Promise<LocationData | null> => {
      try {
        setIsLoading(true);
        setError(null);

        // TODO: need dynamic to use 'es' or 'pt' depend language of platform web
        const language = localStorageService.getLanguage() || 'es';

        return await LocationService.getLocationDataByPlaceId(
          placeId,
          originalText,
          language,
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        console.error('Error getting location data:', errorMessage);
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    getLocationData,
    isLoading,
    error,
  };
}
